/**
 * Staff MobilePay is a Clinio Pay payment request for the outstanding balance.
 * A request is not an invoice_payments row. The webhook is what records the payment.
 */

export type MobilePayRequestBlock = 'disabled' | 'unavailable' | 'nothing_due';

export const CHECKOUT_PAYABLE_STATUSES = [
  'issued',
  'partial',
  'overdue',
  'expired_pay_link',
] as const;

export function isCheckoutPayableStatus(status: string): boolean {
  return (CHECKOUT_PAYABLE_STATUSES as readonly string[]).includes(status);
}

export function outstandingCheckoutCents(
  totalCents: number,
  totalPaidCents: number | null | undefined,
): number {
  const paid = totalPaidCents ?? 0;
  if (!Number.isFinite(totalCents) || !Number.isFinite(paid)) return 0;
  return Math.max(0, Math.round(totalCents) - Math.round(paid));
}

/**
 * Stable per invoice and outstanding amount, so a second click reuses the
 * same Clinio Pay idempotency key. An expired link uses a retry key so the
 * next request is not stuck on the expired checkout. A further expiry of that
 * retry reuses the same retry key.
 */
export function checkoutIdempotencyMaterial(input: {
  invoiceId: string;
  amountCents: number;
  status: string;
  requestGeneration?: number;
}): string {
  const generation = input.requestGeneration ?? 0;
  const base =
    generation > 0
      ? `${input.invoiceId}:${input.amountCents}:g${generation}`
      : `${input.invoiceId}:${input.amountCents}`;
  if (input.status === 'expired_pay_link') return `${base}:retry`;
  return base;
}

export function mobilePayRequestBlock(input: {
  ready: boolean;
  mobilePayEnabled: boolean;
  clinioPayAvailable: boolean;
  outstandingCents: number;
}): MobilePayRequestBlock | null {
  if (!input.ready) return null;
  if (input.outstandingCents <= 0) return 'nothing_due';
  if (!input.mobilePayEnabled) return 'disabled';
  if (!input.clinioPayAvailable) return 'unavailable';
  return null;
}

export function mobilePayRequestErrorKey(block: MobilePayRequestBlock): string {
  switch (block) {
    case 'disabled':
      return 'invoices.mobilepay_request_disabled';
    case 'unavailable':
      return 'invoices.mobilepay_request_unavailable';
    case 'nothing_due':
      return 'invoices.mobilepay_request_nothing_due';
    default: {
      const _exhaustive: never = block;
      return _exhaustive;
    }
  }
}

/** Issued or overdue, nothing collected yet, and the provider method is already chosen. */
export function showsAwaitingProviderPayment(input: {
  status: string;
  paymentMethod?: string | null;
  totalPaidCents?: number | null;
}): boolean {
  if (input.paymentMethod !== 'mobilepay' && input.paymentMethod !== 'card') return false;
  if ((input.totalPaidCents ?? 0) > 0) return false;
  return input.status === 'issued' || input.status === 'overdue';
}

/** Staff can drop an unpaid card or MobilePay request and choose another method. */
export function canCancelProviderPaymentRequest(input: {
  status: string;
  paymentMethod?: string | null;
  totalPaidCents?: number | null;
}): boolean {
  return showsAwaitingProviderPayment(input);
}

export function providerPayPageUrl(origin: string, payToken: string): string {
  const base = origin.replace(/\/$/, '');
  return `${base}/pay-invoice/${payToken}`;
}
